/**
 * Immutable Audit Trail System
 * 
 * Maritime Compliance Requirement:
 * - All changes must be logged (append-only, immutable)
 * - No hard deletes - only soft deletes (deletedAt timestamp)
 * - Complete history of all changes
 * - Regulatory compliance (SOLAS, MARPOL, ISM, MLC 2006)
 * 
 * Best Practices:
 * - Append-only: Never modify or delete audit records
 * - Immutable: Audit records cannot be changed once created
 * - Complete: Log before/after state, user, timestamp, reason
 * - Queryable: Fast access to change history
 */

import { db } from './db';
import { createLogger } from '../utils/logger';

const logger = createLogger('AuditTrail');

/**
 * Audit Trail Entry
 * Immutable record of a change to any entity
 */
export interface AuditTrailEntry {
  id: string;
  entityType: string;        // Table name (e.g., 'certificates', 'personnel')
  entityId: string;          // ID of the changed entity
  operation: 'create' | 'update' | 'delete' | 'restore';
  userId: string;            // User who made the change
  userName?: string;          // User name for display
  userRole?: string;          // User role for context
  timestamp: Date;            // When the change occurred
  beforeState?: any;          // Complete state before change (JSON)
  afterState?: any;           // Complete state after change (JSON)
  changedFields?: string[];   // List of fields that changed
  reason?: string;            // Reason for change (if provided)
  ipAddress?: string;         // IP address (if available)
  userAgent?: string;          // User agent (if available)
  organizationId?: string;     // Organization context
  vesselId?: string;          // Vessel context (if applicable)
  createdAt: Date;            // When audit record was created (immutable)
}

/**
 * Audit Trail Service
 * Handles all audit logging with append-only, immutable records
 */
export class AuditTrailService {
  /**
   * Log a change to the audit trail
   * This is append-only - records are never modified or deleted
   */
  static async logChange(params: {
    entityType: string;
    entityId: string;
    operation: 'create' | 'update' | 'delete' | 'restore';
    userId: string;
    userName?: string;
    userRole?: string;
    beforeState?: any;
    afterState?: any;
    changedFields?: string[];
    reason?: string;
    ipAddress?: string;
    userAgent?: string;
    organizationId?: string;
    vesselId?: string;
  }): Promise<void> {
    try {
      const auditEntry: AuditTrailEntry = {
        id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        entityType: params.entityType,
        entityId: params.entityId,
        operation: params.operation,
        userId: params.userId,
        userName: params.userName,
        userRole: params.userRole,
        timestamp: new Date(),
        beforeState: params.beforeState ? JSON.parse(JSON.stringify(params.beforeState)) : undefined,
        afterState: params.afterState ? JSON.parse(JSON.stringify(params.afterState)) : undefined,
        changedFields: params.changedFields,
        reason: params.reason,
        ipAddress: params.ipAddress,
        userAgent: params.userAgent,
        organizationId: params.organizationId,
        vesselId: params.vesselId,
        createdAt: new Date(),
      };

      // Store in audit trail table (append-only, immutable)
      // This table is never modified or deleted - only appended to
      await db.auditTrail.add(auditEntry as any);

      // Also sync to Supabase for cloud backup
      // This will be handled by the sync queue

      logger.info('Audit trail entry created', {
        entityType: params.entityType,
        entityId: params.entityId,
        operation: params.operation,
      });
    } catch (error) {
      // Audit logging should never fail silently
      logger.error('Failed to create audit trail entry', error);
      // Don't throw - we don't want audit failures to break operations
      // But log it prominently
    }
  }

  /**
   * Get audit trail for a specific entity
   */
  static async getEntityAuditTrail(
    entityType: string,
    entityId: string
  ): Promise<AuditTrailEntry[]> {
    try {
      const entries = await db.auditTrail
        .where('[entityType+entityId]')
        .equals([entityType, entityId])
        .sortBy('timestamp');

      return entries.reverse(); // Most recent first
    } catch (error) {
      logger.error('Failed to get audit trail', error);
      return [];
    }
  }

  /**
   * Get audit trail for a user
   */
  static async getUserAuditTrail(userId: string): Promise<AuditTrailEntry[]> {
    try {
      const entries = await db.auditTrail
        .where('userId')
        .equals(userId)
        .sortBy('timestamp');

      return entries.reverse(); // Most recent first
    } catch (error) {
      logger.error('Failed to get user audit trail', error);
      return [];
    }
  }

  /**
   * Get audit trail for an entity type
   */
  static async getEntityTypeAuditTrail(
    entityType: string,
    limit: number = 100
  ): Promise<AuditTrailEntry[]> {
    try {
      const entries = await db.auditTrail
        .where('entityType')
        .equals(entityType)
        .sortBy('timestamp');

      return entries.slice(-limit).reverse(); // Most recent first, limited
    } catch (error) {
      logger.error('Failed to get entity type audit trail', error);
      return [];
    }
  }

  /**
   * Get changes for a specific field
   */
  static async getFieldHistory(
    entityType: string,
    entityId: string,
    fieldName: string
  ): Promise<Array<{ timestamp: Date; oldValue: any; newValue: any; userId: string }>> {
    try {
      const entries = await this.getEntityAuditTrail(entityType, entityId);
      
      return entries
        .filter(entry => 
          entry.changedFields?.includes(fieldName) ||
          (entry.beforeState && entry.afterState && 
           entry.beforeState[fieldName] !== entry.afterState[fieldName])
        )
        .map(entry => ({
          timestamp: entry.timestamp,
          oldValue: entry.beforeState?.[fieldName],
          newValue: entry.afterState?.[fieldName],
          userId: entry.userId,
        }));
    } catch (error) {
      logger.error('Failed to get field history', error);
      return [];
    }
  }

  /**
   * Detect changed fields between two states
   */
  static detectChangedFields(before: any, after: any): string[] {
    if (!before) return Object.keys(after || {});
    if (!after) return Object.keys(before || {});

    const changed: string[] = [];
    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);

    for (const key of allKeys) {
      // Skip internal fields
      if (key === 'updatedAt' || key === 'updated_at') continue;
      
      if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
        changed.push(key);
      }
    }

    return changed;
  }
}

